import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import type { Page } from "@inertiajs/core";
import { Head, router, useForm, usePage } from "@inertiajs/react";
import {
  adminDestinationLocationExclusionsPath,
  adminDestinationLocationInclusionPath,
  adminDestinationLocationInclusionsPath,
  adminDestinationPath,
  adminDestinationPhotoPath,
  adminDestinationPhotosPath,
  adminDestinationReorderingPath,
  adminDestinationStockPhotoPath,
  adminDestinationsPath,
  adminDestinationsStockPhotoSearchesPath,
  destinationPath,
  editAdminDestinationPath,
} from "@travily/routes";
import type AdminDestinationsEdit from "@travily/types/pages/AdminDestinationsEdit";
import { type ReactNode, useCallback, useMemo } from "react";
import { PhotoSection } from "@/components/admin/PhotoSection";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useAdminPhotoManager } from "@/hooks/admin/use-admin-photo-manager";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { useMap } from "@/layouts/map-host";
import { AddLocations } from "./components/add-locations";
import {
  ADMIN_MAP_BOUNDS_PADDING,
  ADMIN_MAP_CLASSNAME,
  type BboxCoords,
  BboxRectangle,
  bboxToEnvelope,
} from "./components/bbox-rectangle";
import { type LocationPin, LocationPins } from "./components/location-pins";
import { EditView } from "./edit.view";

interface BboxEditorMapProps {
  fitBbox: BboxCoords;
  bbox: BboxCoords;
  onBboxChange: (next: BboxCoords) => void;
}

function BboxEditorMap({ fitBbox, bbox, onBboxChange }: BboxEditorMapProps) {
  const envelope = useMemo(() => bboxToEnvelope(fitBbox), [fitBbox]);
  const overlay = useMemo(
    () => <BboxRectangle bbox={bbox} editable onBboxChange={onBboxChange} />,
    [bbox, onBboxChange],
  );
  const slotRef = useMap({
    overlay,
    className: ADMIN_MAP_CLASSNAME,
    fitBounds: envelope,
    fitBoundsPadding: ADMIN_MAP_BOUNDS_PADDING,
  });
  return <div ref={slotRef} />;
}

interface LocationsMapProps {
  bbox: BboxCoords;
  locations: LocationPin[];
}

function LocationsMap({ bbox, locations }: LocationsMapProps) {
  const activePoints = useMemo(() => locations.filter((p) => p.link_type !== "excluded"), [locations]);
  const envelope = useMemo(() => bboxToEnvelope(bbox, activePoints), [bbox, activePoints]);
  const overlay = useMemo(
    () => (
      <>
        <BboxRectangle bbox={bbox} />
        <LocationPins locations={locations} />
      </>
    ),
    [bbox, locations],
  );
  const slotRef = useMap({
    overlay,
    className: ADMIN_MAP_CLASSNAME,
    fitBounds: envelope,
    fitBoundsPadding: ADMIN_MAP_BOUNDS_PADDING,
  });
  return <div ref={slotRef} />;
}

function extractDestinationPhotos(page: Page) {
  const props = page.props as Record<string, unknown>;
  const destination = props.destination as Record<string, unknown> | undefined;
  return Array.isArray(destination?.photos)
    ? (destination.photos as AdminDestinationsEdit["destination"]["photos"])
    : null;
}

type AdminDestinationsEditPageProps = AdminDestinationsEdit;

function AdminDestinationsEditPage({
  destination,
  destination_locations,
  available_countries,
}: AdminDestinationsEditPageProps) {
  const account = useAdminLayoutAccount();

  const form = useForm({
    name: destination.name,
    country_code: destination.country_code,
    description: destination.description ?? "",
    bbox_north: destination.bbox_north,
    bbox_south: destination.bbox_south,
    bbox_east: destination.bbox_east,
    bbox_west: destination.bbox_west,
  });
  const { data, setData, patch, processing, transform } = form;
  transform((formData) => ({ destination: formData }));

  const photoManager = useAdminPhotoManager({
    initialPhotos: destination.photos,
    paths: {
      indexPath: adminDestinationPhotosPath(destination.id),
      photoPath: (sqid: string) => adminDestinationPhotoPath(destination.id, sqid),
      reorderPath: adminDestinationReorderingPath(destination.id),
      createStockPath: adminDestinationStockPhotoPath(destination.id),
      stockSearchPath: adminDestinationsStockPhotoSearchesPath(),
    },
    extractPhotos: extractDestinationPhotos,
  });

  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch(adminDestinationPath(destination.id));
  };

  const handleResetBbox = () => {
    if (
      destination.mapbox_bbox_north == null ||
      destination.mapbox_bbox_south == null ||
      destination.mapbox_bbox_east == null ||
      destination.mapbox_bbox_west == null
    )
      return;

    setData({
      ...data,
      bbox_north: destination.mapbox_bbox_north,
      bbox_south: destination.mapbox_bbox_south,
      bbox_east: destination.mapbox_bbox_east,
      bbox_west: destination.mapbox_bbox_west,
    });
  };

  const handleLocationAction = (action: "include" | "exclude" | "remove", locationId: number) => {
    const options = { preserveScroll: true };

    if (action === "remove") {
      router.delete(adminDestinationLocationInclusionPath(destination.id, locationId), options);
    } else if (action === "exclude") {
      router.post(adminDestinationLocationExclusionsPath(destination.id), { location_id: locationId }, options);
    } else {
      router.post(adminDestinationLocationInclusionsPath(destination.id), { location_id: locationId }, options);
    }
  };

  const bbox: BboxCoords = useMemo(
    () => ({
      north: destination.bbox_north,
      south: destination.bbox_south,
      east: destination.bbox_east,
      west: destination.bbox_west,
    }),
    [destination.bbox_north, destination.bbox_south, destination.bbox_east, destination.bbox_west],
  );

  const activeLocations = useMemo(
    () => destination_locations.filter((dl) => dl.link_type !== "excluded"),
    [destination_locations],
  );
  const excludedLocations = useMemo(
    () => destination_locations.filter((dl) => dl.link_type === "excluded"),
    [destination_locations],
  );
  const locationPins = useMemo<LocationPin[]>(
    () =>
      destination_locations.map((dl) => ({
        latitude: dl.location.latitude,
        longitude: dl.location.longitude,
        link_type: dl.link_type,
        name: dl.location.name,
      })),
    [destination_locations],
  );

  const formBbox = useMemo<BboxCoords>(
    () => ({
      north: data.bbox_north,
      south: data.bbox_south,
      east: data.bbox_east,
      west: data.bbox_west,
    }),
    [data.bbox_north, data.bbox_south, data.bbox_east, data.bbox_west],
  );

  const handleBboxChange = useCallback(
    (next: BboxCoords) => {
      setData((prev) => ({
        ...prev,
        bbox_north: next.north,
        bbox_south: next.south,
        bbox_east: next.east,
        bbox_west: next.west,
      }));
    },
    [setData],
  );

  const hasMapboxBbox =
    destination.mapbox_bbox_north != null &&
    destination.mapbox_bbox_south != null &&
    destination.mapbox_bbox_east != null &&
    destination.mapbox_bbox_west != null;

  const bboxMatchesMapbox =
    hasMapboxBbox &&
    data.bbox_north === destination.mapbox_bbox_north &&
    data.bbox_south === destination.mapbox_bbox_south &&
    data.bbox_east === destination.mapbox_bbox_east &&
    data.bbox_west === destination.mapbox_bbox_west;

  const defaultTab = new URLSearchParams(usePage().url.split("?")[1]).get("tab") ?? "details";

  const handleTabChange = (key: string) => {
    router.get(editAdminDestinationPath(destination.id), { tab: key }, { preserveState: true, preserveScroll: true });
  };

  return (
    <AdminLayout activeUrl={adminDestinationsPath()} account={account}>
      <Head title={`${destination.name} - Admin`} />

      <AdminPageHeader
        breadcrumbs={[{ label: "Destinations", href: adminDestinationsPath() }, { label: destination.name }]}
        title={destination.name}
        actions={
          <Button
            size="sm"
            color="secondary"
            href={destinationPath(destination.country_slug, destination.slug)}
            target="_blank"
          >
            View destination
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-container px-4 lg:px-8">
        <EditView
          defaultTab={defaultTab}
          onTabChange={handleTabChange}
          name={data.name}
          countryCode={data.country_code}
          description={data.description}
          availableCountries={available_countries}
          processing={processing}
          cancelHref={adminDestinationsPath()}
          onNameChange={(value) => setData("name", value)}
          onCountryCodeChange={(value) => setData("country_code", value)}
          onDescriptionChange={(value) => setData("description", value)}
          onDetailsSubmit={handleDetailsSubmit}
          photoSection={<PhotoSection manager={photoManager} />}
          showResetBbox={hasMapboxBbox && !bboxMatchesMapbox}
          onResetBbox={handleResetBbox}
          bboxEditorMap={<BboxEditorMap fitBbox={bbox} bbox={formBbox} onBboxChange={handleBboxChange} />}
          bbox={bbox}
          activeLocations={activeLocations}
          excludedLocations={excludedLocations}
          locationsMap={<LocationsMap bbox={bbox} locations={locationPins} />}
          addLocations={
            <AddLocations
              destinationId={destination.id}
              linkedLocationCount={destination_locations.length}
              onInclude={(locationId) => handleLocationAction("include", locationId)}
            />
          }
          onExcludeLocation={(locationId) => handleLocationAction("exclude", locationId)}
          onUnlinkLocation={(locationId) => handleLocationAction("remove", locationId)}
          onReincludeLocation={(locationId) => handleLocationAction("include", locationId)}
        />
      </div>
    </AdminLayout>
  );
}

AdminDestinationsEditPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminDestinationsEditPage;
